import { useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  ArrowsClockwiseIcon,
  DotsThreeVerticalIcon,
  LinkBreakIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import {
  DetachTaskFromAgentDialog,
  type DetachTaskSnapshot,
} from 'src/components/tasks/DetachTaskFromAgentDialog';
import {
  useDetachableAgentId,
  type DetachableTask,
} from 'src/components/tasks/detachFromAgent';
import { SendTaskBackToAgentDialog } from './SendTaskBackToAgentDialog';
import { useSendBackToAgentPlan } from './useSendBackToAgentPlan';
import type {
  SendBackTaskSnapshot,
  SendBackTaskTypeSnapshot,
} from './sendBackToAgentPlan';
import { DeleteTaskDialog } from './TaskDeleteControls';
import { useCanDeleteTask, type DeletableTask } from './taskDeletion';

type HeaderMenuDialog = 'send-back' | 'detach' | 'delete';

/**
 * The task header's `⋮` overflow menu. The trigger renders only when at least
 * one of its actions applies. The dialogs sit outside the menu so they outlive
 * it, and the delete dialog stays mounted even once the refetched task is
 * deleted (and `canDelete` turns false), so it can finish closing.
 */
export function TaskHeaderMenu({
  task,
  taskType,
  agents,
}: {
  task: SendBackTaskSnapshot &
    DeletableTask &
    DetachableTask &
    DetachTaskSnapshot;
  taskType: SendBackTaskTypeSnapshot | null | undefined;
  agents: Array<{ id: string; name: string }>;
}) {
  const gt = useGT();
  const [dialog, setDialog] = useState<HeaderMenuDialog | null>(null);
  const sendBackPlan = useSendBackToAgentPlan(task, taskType);
  const canDelete = useCanDeleteTask(task);
  const detachAgentId = useDetachableAgentId(task);
  const hasAgentActions = sendBackPlan !== null || detachAgentId !== null;
  const hasActions = hasAgentActions || canDelete;
  const detachAgentName =
    agents.find((agent) => agent.id === detachAgentId)?.name ??
    gt('the agent', {
      context:
        'fallback when the name of the agent a task is detached from is not loaded',
    });

  function dialogOpenChange(which: HeaderMenuDialog) {
    return (open: boolean) => setDialog(open ? which : null);
  }

  return (
    <>
      {hasActions && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              data-testid="task-overflow-menu"
              aria-label={gt('More task actions', {
                context: 'accessible label for the task header overflow menu',
              })}
            >
              <DotsThreeVerticalIcon className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {sendBackPlan && (
              <DropdownMenuItem
                data-testid="send-task-back-to-agent"
                onSelect={() => setDialog('send-back')}
              >
                <ArrowsClockwiseIcon className="size-4" />
                <T context="menu item on the task page that opens confirmation to re-queue the task for its task type's default agent">
                  Send Back to Agent
                </T>
              </DropdownMenuItem>
            )}
            {detachAgentId && (
              <DropdownMenuItem
                data-testid="detach-task-from-agent"
                onSelect={() => setDialog('detach')}
              >
                <LinkBreakIcon className="size-4" />
                <T context="menu item on the task page that opens confirmation to take the task away from its agent">
                  Detach from Agent…
                </T>
              </DropdownMenuItem>
            )}
            {hasAgentActions && canDelete && <DropdownMenuSeparator />}
            {canDelete && (
              <DropdownMenuItem
                variant="destructive"
                data-testid="delete-task"
                onSelect={() => setDialog('delete')}
              >
                <TrashIcon className="size-4" />
                <T context="menu item that opens confirmation to delete the task">
                  Delete…
                </T>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {sendBackPlan && (
        <SendTaskBackToAgentDialog
          taskId={task.id}
          plan={sendBackPlan}
          agents={agents}
          open={dialog === 'send-back'}
          onOpenChange={dialogOpenChange('send-back')}
        />
      )}
      {detachAgentId && (
        <DetachTaskFromAgentDialog
          task={task}
          agentName={detachAgentName}
          open={dialog === 'detach'}
          onOpenChange={dialogOpenChange('detach')}
        />
      )}
      <DeleteTaskDialog
        taskId={task.id}
        open={dialog === 'delete'}
        onOpenChange={dialogOpenChange('delete')}
      />
    </>
  );
}
